import {Button, IconButton} from "@mui/material";
import {useCallback} from "react";
import {FiPlus, FiTrash2} from "react-icons/fi";
import {useLocation, useNavigate} from "react-router-dom";
import ListView from "../components/ListView/ListView";
import Window from "../components/Window/Window";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import SearchBar from "../components/ui/SearchBar";
import {clientColumns} from "../constants/dataGridColumns";
import {clientSearchFields} from "../constants/searchFields";
import useApiRequest from "../hooks/useApiRequest";
import useConfirmDialog from "../hooks/useConfirmDialog";
import {useAuthPermissions, ACTIONS, RESOURCES} from "../hooks/useAuthPermissions";
import usePaginationSearch from "../hooks/usePaginationSearch";
import {snackbar} from "../utils/snackbar";

const Clients = () => {
  const navigate = useNavigate();
  const {state} = useLocation();
  const {list, remove} = useApiRequest({baseUrl: "/clients", resource: "clients"});
  const {can} = useAuthPermissions();

  const fetcher = useCallback(
    async (params) => {
      return await list(params);
    },
    [list]
  );

  const {filters, applyFilters, resetFilters, refresh, dataGridProps, listState} = usePaginationSearch({
    fetcher,
    initialFilters: {},
    initialListState: state?.listState
  });

  // Hook para el diálogo de confirmación
  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Cliente eliminado correctamente.");
    refresh();
  });

  const handleRowClick = (client) => {
    if (!client?.client_id) {
      snackbar.error("No se ha podido abrir el cliente seleccionado.");
      return;
    }
    navigate(`/client-form`, {state: {id: client?.client_id, from: {pathname: "/clients", listState}}});
  };

  const handleOpenDeleteDialog = (id) => {
    const rows = dataGridProps.rows || [];
    const found = rows.find((client) => client.client_id === id);
    openDialog(id, found?.name || "");
  };

  const columns = [
    ...clientColumns,
    ...(can(ACTIONS.DELETE, RESOURCES.CLIENTS)
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <div style={{display: "flex", alignItems: "center", justifyContent: "center", height: "100%"}}>
                <IconButton
                  aria-label="delete"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteDialog(params.row.client_id);
                  }}
                >
                  <FiTrash2 />
                </IconButton>
              </div>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      <Window
        title={"Clientes"}
        topRightButtons={
          can(ACTIONS.WRITE, RESOURCES.CLIENTS) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FiPlus />}
              onClick={() => {
                navigate(`/client-form`);
              }}
            >
              Nuevo cliente
            </Button>
          )
        }
      >
        {/* Barra de filtros */}
        <SearchBar
          fields={clientSearchFields}
          initialValues={filters}
          onSearch={applyFilters}
          onReset={resetFilters}
        />

        <ListView
          columns={columns}
          getRowId={(row) => row.client_id}
          onRowClick={handleRowClick}
          dataGridProps={{
            ...dataGridProps,
            getRowHeight: () => "auto"
          }}
        />
      </Window>

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="cliente"
        entityLabel={itemName}
      />
    </>
  );
};

export default Clients;
